import { create } from "zustand";
import { persist } from "zustand/middleware";
import { createSafeLocalStorage } from "./persistStorage";
import type {
  UploadItem,
  UploadStatus,
  UploadItemKind,
  UploadFlow,
  UploadTargetSpaceCaptureSource,
  EditorImportRuntimeState,
  EditorImportAdmission,
  PlatformFile,
  FileData,
} from "../types";
import {
  getObjectImportMilestone,
  getObjectImportStatusRank,
  getObjectImportUiStage,
  OBJECT_IMPORT_MIN_VISIBLE_STAGE_MS,
} from "../utils/objectImportProgress";
import { skipsFileConversionJobMonitoring } from "../utils/uploadStopBehavior";

export const createEditorImportIdentity = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `import:${Date.now()}:${Math.random().toString(36).slice(2)}`;

export const canonicalEditorImportProject = (value: string | null | undefined): string | null => {
  const path = value?.trim();
  return path && /^(gs|s3):\/\/[^/]+\/.+/.test(path) && !/[?#]/.test(path) ? path : null;
};

export const hasEditorImportAdmission = (item: Partial<UploadItem> | null | undefined): boolean =>
  item?.editorImportAdmission?.state === "held" || item?.editorImportAdmission?.state === "reconciling";

// Space에는 영속 ID가 없어 target을 index·이름으로만 확인한다. 삭제·재정렬·복제로 target
// 자리가 바뀌면 같은 index·이름의 다른 Space가 검사를 통과하므로(QA I-12), 어떤 Space와도
// 일치하지 않는 이름으로 바꿔 기존 target 이름 검사가 모두 명시적 실패로 끝나게 한다.
// ponytail: 다른 탭·기기나 Editor 밖에서의 변경은 이벤트가 없어 못 막는다. 필요하면 VSData에 영속 id를 추가한다.
export const INVALIDATED_EDITOR_IMPORT_TARGET_SPACE_NAME = "\u0000target-space-moved";

const editorImportTargetFinalStatuses = new Set(["completed", "failed", "failed_capacity_post_conversion", "cancelled"]);

export const isEditorImportExecutionCurrent = (id: string, executionId: string): boolean => {
  const item = useUploadStore.getState().items[id];
  return hasEditorImportAdmission(item) && item.editorImportAdmission?.executionId === executionId && !item.cancelled && !item.paused;
};

// UploadItem에 projectTitle 필드 추가 (기존 타입 확장)
export interface ExtendedUploadItem extends UploadItem {
  projectTitle?: string | null;
  loaderValidation?: LoaderValidationResultSummary | null;
  conversionFailure?: ConversionFailureResultSummary | null;
}

export interface LoaderValidationResultSummary {
  attemptKey: string;
  loaderJobId: string;
  generation: number;
  outcome: "success" | "failed";
  status: "passed" | "warning" | "failed";
  eventCodes: string[];
  fileName?: string | null;
  projection?: ConversionValidationProjectionSummary | null;
}

export interface ConversionCheckGroupSummary {
  state: "no_issues" | "needs_review" | "not_checked";
  total: number | null;
  replaced_count?: number;
  missing_count?: number;
  may_differ?: boolean;
}

export interface ConversionValidationProjectionSummary {
  filename: string | null;
  original_size_bytes: number | null;
  vse_size_bytes: number | null;
  parts_count: number | null;
  converter_outcome: "success" | "warning" | "failed";
  quality_state: "no_issues" | "needs_review" | "not_checked" | "failed";
  loader_outcome: "not_checked" | "success" | "failed";
  object_size: {
    width: number | null;
    height: number | null;
    depth: number | null;
    unit: string | null;
  };
  materials: ConversionCheckGroupSummary;
  textures: ConversionCheckGroupSummary;
  warning_codes: string[];
}

export type ConversionFailureStage =
  "uploading" | "converting" | "optimizing" | "rendering";

export interface ConversionFailureResultSummary {
  source: "converter" | "runtime";
  status: "failed";
  stage: ConversionFailureStage;
  fileName?: string | null;
}

export type ConversionReportResultSummary =
  LoaderValidationResultSummary | ConversionFailureResultSummary;

// Upsert data type (partial UploadItem)
interface UpsertItemData {
  editorImportAdmission?: EditorImportAdmission | null;
  id?: string;
  fileName?: string;
  fileUrl?: string | null;
  storagePath?: string | null;
  gcsPath?: string | null;
  finalGcsPath?: string | null;
  convertedDownloadUrl?: string | null;
  size?: number | null;
  projectTitle?: string | null;
  status?: UploadStatus;
  progress?: number;
  error?: string | null;
  paused?: boolean;
  cancelled?: boolean;
  targetSpaceIndex?: number | null;
  targetSpaceName?: string | null;
  targetSpaceCaptureSource?: UploadTargetSpaceCaptureSource | null;
  targetSpaceCapturedTransactionId?: string | null;
  targetSpaceCapturedPhase?: string | null;
  originViewrGcsPath?: string | null;
  originViewrTitle?: string | null;
  originSpaceIndex?: number | null;
  originRoute?: string | null;
  storageUsageSnapshotBytes?: number | null;
  jobId?: string | null;
  conversionLogTest?: string | null;
  platformFiles?: PlatformFile[] | null;
  fileData?: FileData | Record<string, unknown> | null;
  kind?: UploadItemKind;
  flow?: UploadFlow;
  editorImportVseIndex?: number | null;
  editorImportRuntimeState?: EditorImportRuntimeState | null;
  objectImportUiStageStartedAt?: number | null;
  loaderValidation?: LoaderValidationResultSummary | null;
  conversionFailure?: ConversionFailureResultSummary | null;
}

interface FallbackIdParams {
  jobId?: string | null;
  storagePath?: string | null;
  fileUrl?: string | null;
  fileName?: string | null;
}

function fallbackId({
  jobId,
  storagePath,
  fileUrl,
  fileName,
}: FallbackIdParams): string {
  return (
    jobId ||
    storagePath ||
    fileUrl ||
    `local:${fileName || "file"}:${Date.now()}`
  );
}

// Upload Store State
interface UploadStoreState {
  items: Record<string, ExtendedUploadItem>;

  // Actions
  reserveEditorImport: (data: UpsertItemData & { id: string; fileName: string; originViewrGcsPath: string; targetSpaceIndex: number }) => string | null;
  beginEditorImportExecution: (id: string) => string | null;
  updateEditorImportAdmission: (id: string, executionId: string, patch: Partial<EditorImportAdmission>) => boolean;
  releaseEditorImport: (args: { id: string; executionId?: string; jobId?: string; runtimeExecutionId?: string; reason: "pre_submit_failure" | "remote_terminal" | "runtime_complete" | "runtime_cleanup" | "terminal_failure" | "runtime_failure" }) => boolean;
  invalidateEditorImportTargets: (project: string | null | undefined, isMoved: (targetSpaceIndex: number) => boolean) => void;
  upsertItem: (data: UpsertItemData) => string;
  startUpload: (params: {
    id?: string;
    fileName: string;
    storagePath?: string | null;
    gcsPath?: string | null;
    size?: number | null;
    projectTitle?: string | null;
    targetSpaceIndex?: number | null;
    targetSpaceName?: string | null;
    originViewrGcsPath?: string | null;
    originViewrTitle?: string | null;
    originSpaceIndex?: number | null;
    originRoute?: string | null;
    storageUsageSnapshotBytes?: number | null;
    kind?: UploadItemKind;
    flow?: UploadFlow;
    targetSpaceCaptureSource?: UploadTargetSpaceCaptureSource | null;
    targetSpaceCapturedTransactionId?: string | null;
    targetSpaceCapturedPhase?: string | null;
    editorImportVseIndex?: number | null;
    editorImportRuntimeState?: EditorImportRuntimeState | null;
  }) => string;
  startDownload: (params: {
    id?: string;
    fileName: string;
    gcsPath?: string | null;
    flow?: UploadFlow;
  }) => string;
  setUploaded: (params: { id?: string; storagePath: string }) => string;
  setConverting: (params: {
    id?: string;
    jobId?: string | null;
    storagePath?: string | null;
    progress?: number;
  }) => string;
  setOptimizing: (params: { id: string }) => string;
  setBundling: (params: { id: string }) => string;
  setRendering: (params: { id: string; progress?: number }) => string;
  setFinalizing: (params: { id: string }) => string;
  setProcessing: (params: {
    id: string;
    message?: string;
    progress?: number;
  }) => string;
  setProgress: (params: { id: string; progress: number }) => string;
  complete: (params: {
    id: string;
    fileUrl?: string | null;
    gcsPath?: string | null;
    finalGcsPath?: string | null;
    convertedDownloadUrl?: string | null;
    jobId?: string | null;
    platformFiles?: PlatformFile[] | null;
    fileData?: FileData | null;
    kind?: UploadItemKind;
  }) => string;
  fail: (params: {
    id: string;
    error: string;
    status?: UploadStatus;
  }) => string;
  pause: (id: string) => string;
  resume: (id: string) => void;
  cancel: (id: string) => string;
  remove: (id: string) => void;
  clearAll: () => void;
  cleanupOldItems: () => void;
}

interface DeferredObjectImportTransition {
  data: UpsertItemData;
  uiStage: NonNullable<ReturnType<typeof getObjectImportUiStage>>;
}

interface DeferredObjectImportQueue {
  transitions: DeferredObjectImportTransition[];
  timeoutId: ReturnType<typeof setTimeout> | null;
}

const deferredObjectImportQueues = new Map<string, DeferredObjectImportQueue>();
const applyingDeferredObjectImportIds = new Set<string>();

const clearDeferredObjectImportQueue = (id: string): void => {
  const queue = deferredObjectImportQueues.get(id);
  if (queue?.timeoutId != null) {
    clearTimeout(queue.timeoutId);
  }
  deferredObjectImportQueues.delete(id);
};

const clearAllDeferredObjectImportQueues = (): void => {
  deferredObjectImportQueues.forEach((queue) => {
    if (queue.timeoutId != null) {
      clearTimeout(queue.timeoutId);
    }
  });
  deferredObjectImportQueues.clear();
  applyingDeferredObjectImportIds.clear();
};

const scheduleDeferredObjectImportTransition = (
  id: string,
  get: () => UploadStoreState,
  delayMs: number,
): void => {
  const queue = deferredObjectImportQueues.get(id);
  if (!queue || queue.timeoutId != null) return;

  queue.timeoutId = setTimeout(
    () => {
      const currentQueue = deferredObjectImportQueues.get(id);
      if (!currentQueue) return;
      currentQueue.timeoutId = null;

      const nextTransition = currentQueue.transitions.shift();
      if (!nextTransition) {
        deferredObjectImportQueues.delete(id);
        return;
      }

      applyingDeferredObjectImportIds.add(id);
      try {
        get().upsertItem(nextTransition.data);
      } finally {
        applyingDeferredObjectImportIds.delete(id);
      }

      if (currentQueue.transitions.length === 0) {
        deferredObjectImportQueues.delete(id);
        return;
      }

      scheduleDeferredObjectImportTransition(
        id,
        get,
        OBJECT_IMPORT_MIN_VISIBLE_STAGE_MS,
      );
    },
    Math.max(0, delayMs),
  );
};

const deferObjectImportTransitionIfNeeded = (
  id: string,
  data: UpsertItemData,
  get: () => UploadStoreState,
): boolean => {
  if (applyingDeferredObjectImportIds.has(id) || data.status == null) {
    return false;
  }

  const prev = get().items[id];
  const nextFlow = data.flow ?? prev?.flow;
  if (nextFlow !== "editor_object_import") {
    return false;
  }

  const previousUiStage = getObjectImportUiStage(prev?.status);
  const nextUiStage = getObjectImportUiStage(data.status);
  const queue = deferredObjectImportQueues.get(id);
  if (previousUiStage == null || nextUiStage == null) {
    if (data.status === "paused" && queue?.transitions.length) {
      const latestTransition = queue.transitions[queue.transitions.length - 1];
      clearDeferredObjectImportQueue(id);
      applyingDeferredObjectImportIds.add(id);
      try {
        get().upsertItem(latestTransition.data);
      } finally {
        applyingDeferredObjectImportIds.delete(id);
      }
      return false;
    }
    clearDeferredObjectImportQueue(id);
    return false;
  }

  const lastQueuedTransition = queue?.transitions[queue.transitions.length - 1];
  const comparisonStage = lastQueuedTransition?.uiStage ?? previousUiStage;
  if (comparisonStage === nextUiStage) {
    if (lastQueuedTransition) {
      lastQueuedTransition.data = {
        ...lastQueuedTransition.data,
        ...data,
        id,
      };
      return true;
    }
    return false;
  }

  const previousRank = getObjectImportStatusRank(prev?.status);
  const nextRank = getObjectImportStatusRank(data.status);
  if (previousRank != null && nextRank != null && nextRank < previousRank) {
    return false;
  }

  const shownAt = prev?.objectImportUiStageStartedAt;
  const elapsedMs =
    typeof shownAt === "number" ? Date.now() - shownAt : Infinity;
  if (!queue && elapsedMs >= OBJECT_IMPORT_MIN_VISIBLE_STAGE_MS) {
    return false;
  }

  const nextQueue = queue ?? { transitions: [], timeoutId: null };
  nextQueue.transitions.push({
    data: { ...data, id },
    uiStage: nextUiStage,
  });
  deferredObjectImportQueues.set(id, nextQueue);

  if (nextQueue.timeoutId == null) {
    scheduleDeferredObjectImportTransition(
      id,
      get,
      queue
        ? OBJECT_IMPORT_MIN_VISIBLE_STAGE_MS
        : OBJECT_IMPORT_MIN_VISIBLE_STAGE_MS - elapsedMs,
    );
  }
  return true;
};

export const UPLOAD_STORE_PERSIST_VERSION = 5;

/** 영속 대상에서 제외하는 fileData 필드. Unity 로드 직전에 metadata_artifact로 다시 받는다. */
const TRANSIENT_FILE_DATA_KEYS = ["vseFileMetaInfo"] as const;

const toPersistedUploadItem = (item: ExtendedUploadItem): ExtendedUploadItem => {
  const fileData = item.fileData as Record<string, unknown> | null | undefined;
  if (!fileData || !TRANSIENT_FILE_DATA_KEYS.some((key) => key in fileData)) return item;
  const persistedFileData = { ...fileData };
  TRANSIENT_FILE_DATA_KEYS.forEach((key) => delete persistedFileData[key]);
  return { ...item, fileData: persistedFileData as ExtendedUploadItem["fileData"] };
};

export const toPersistedUploadItems = (
  items: Record<string, ExtendedUploadItem>,
): Record<string, ExtendedUploadItem> =>
  Object.fromEntries(
    Object.entries(items).map(([id, item]) => [id, toPersistedUploadItem(item)]),
  );

interface PersistedUploadStoreState {
  items: Record<string, ExtendedUploadItem>;
}

/**
 * items 참조가 같으면 같은 영속 객체를 돌려준다. floatingBar처럼 저장 대상이 아닌
 * 상태만 바뀐 setState에서 storage 어댑터가 재직렬화·쓰기를 생략할 수 있게 한다.
 */
let lastPartializedItems: Record<string, ExtendedUploadItem> | null = null;
let lastPartialized: PersistedUploadStoreState | null = null;
const partializeUploadStore = (state: UploadStoreState): PersistedUploadStoreState => {
  if (lastPartialized && lastPartializedItems === state.items) return lastPartialized;
  lastPartializedItems = state.items;
  lastPartialized = { items: toPersistedUploadItems(state.items) };
  return lastPartialized;
};

/**
 * v1 localStorage는 40% 진행 상태를 legacy `deleting_original`로 보관한다.
 * rehydrate 시 canonical `optimizing`으로 옮겨야 복원된 진행 항목이 UI 단계와
 * rank 판정에서 누락되지 않는다.
 * v4는 floatingBar를 영속하지 않고 fileData의 큰 변환 metadata를 제거한다.
 */
export const migrateUploadStorePersistedState = (
  persistedState: unknown,
  version: number,
): UploadStoreState => {
  if (version >= UPLOAD_STORE_PERSIST_VERSION) return persistedState as UploadStoreState;
  const state = persistedState as Partial<UploadStoreState> | null;
  const items = state?.items;
  if (!items) return persistedState as UploadStoreState;
  const migrated: Record<string, ExtendedUploadItem> = {};
  Object.entries(items).forEach(([id, item]) => {
    const status =
      version < 2 && (item.status as string) === "deleting_original"
        ? "optimizing"
        : item.status;
    const failure = item.conversionFailure;
    const conversionFailure =
      version < 2 && failure && (failure.stage as string) === "deleting_original"
        ? { ...failure, stage: "optimizing" as const }
        : failure;
    const admission = item.editorImportAdmission;
    // v5: runtime 로드 실패로 끝난 항목이 예약(held)을 쥔 채 남아 그 Space의 Import `+`가 영구히 사라지던 상태를 푼다.
    const editorImportAdmission =
      version < 5 &&
      item.flow === "editor_object_import" &&
      item.editorImportRuntimeState === "failed" &&
      hasEditorImportAdmission(item)
        ? { ...admission, state: "released" as const, remotePhase: "terminal" as const }
        : admission;
    migrated[id] = toPersistedUploadItem({ ...item, status, conversionFailure, editorImportAdmission });
  });
  const rest = { ...state, items: migrated } as Record<string, unknown>;
  delete rest.floatingBar;
  return rest as unknown as UploadStoreState;
};

export const useUploadStore = create<UploadStoreState>()(
  persist(
    (set, get) => ({
      items: {}, // id -> item

      // Upsert or create a new item
      reserveEditorImport: (data) => {
        const project = canonicalEditorImportProject(data.originViewrGcsPath);
        if (!project || !Number.isInteger(data.targetSpaceIndex) || data.targetSpaceIndex < 0 || get().items[data.id]) return null;
        // Synchronous check + insertion: no await, event, or external effect between them.
        if (Object.values(get().items).some((item) =>
          hasEditorImportAdmission(item) &&
          canonicalEditorImportProject(item.originViewrGcsPath) === project &&
          item.targetSpaceIndex === data.targetSpaceIndex)) return null;
        const executionId = createEditorImportIdentity();
        get().upsertItem({ ...data, originViewrGcsPath: project, status: "uploading", progress: 0,
          editorImportAdmission: { state: "held", executionId, remotePhase: "not_submitted" } });
        return executionId;
      },
      beginEditorImportExecution: (id) => {
        const item = get().items[id];
        if (!hasEditorImportAdmission(item) || item.cancelled) return null;
        const executionId = createEditorImportIdentity();
        get().upsertItem({ id, editorImportAdmission: { ...item.editorImportAdmission!, executionId } });
        return executionId;
      },
      updateEditorImportAdmission: (id, executionId, patch) => {
        const item = get().items[id];
        if (!hasEditorImportAdmission(item) || item.editorImportAdmission!.executionId !== executionId) return false;
        get().upsertItem({ id, editorImportAdmission: { ...item.editorImportAdmission!, ...patch, executionId } });
        return true;
      },
      releaseEditorImport: ({ id, executionId, jobId, runtimeExecutionId, reason }) => {
        const item = get().items[id];
        if (!hasEditorImportAdmission(item)) return false;
        const admission = item.editorImportAdmission!;
        if (!executionId || admission.executionId !== executionId) return false;
        if ((reason === "remote_terminal" || reason === "runtime_cleanup") && (!jobId || item.jobId !== jobId)) return false;
        if (reason === "remote_terminal" && admission.runtimeStarted) return false;
        if (reason === "runtime_cleanup" && (!runtimeExecutionId || admission.runtimeExecutionId !== runtimeExecutionId || (!item.cancelled && executionId !== runtimeExecutionId))) return false;
        if (reason !== "remote_terminal" && !executionId) return false;
        if (reason === "pre_submit_failure" && admission.remotePhase !== "not_submitted") return false;
        if (reason === "terminal_failure" && (admission.remotePhase !== "accepted" || admission.runtimeStarted)) return false;
        // Unity가 로드에 실패한 실행은 예약을 풀어야 그 Space의 Import `+`가 돌아온다. 실패한 실행만 풀고, 새 실행이 시작됐으면 건드리지 않는다.
        if (reason === "runtime_failure" && (!admission.runtimeStarted || admission.runtimeExecutionId !== executionId)) return false;
        get().upsertItem({ id, editorImportAdmission: { ...admission, state: "released", remotePhase: "terminal" } });
        return true;
      },
      invalidateEditorImportTargets: (project, isMoved) => {
        const canonical = canonicalEditorImportProject(project);
        if (!canonical) return;
        set((state) => {
          let changed = false;
          const items = { ...state.items };
          Object.values(state.items).forEach((item) => {
            if (typeof item.targetSpaceIndex !== "number" || !isMoved(item.targetSpaceIndex) ||
              canonicalEditorImportProject(item.originViewrGcsPath) !== canonical ||
              editorImportTargetFinalStatuses.has(item.status) || item.editorImportAdmission?.runtimeStarted ||
              item.targetSpaceName === INVALIDATED_EDITOR_IMPORT_TARGET_SPACE_NAME) return;
            items[item.id] = { ...item, targetSpaceName: INVALIDATED_EDITOR_IMPORT_TARGET_SPACE_NAME };
            changed = true;
          });
          return changed ? { items } : state;
        });
      },
      upsertItem: (data: UpsertItemData) => {
        const id = data.id || fallbackId(data);
        if (deferObjectImportTransitionIfNeeded(id, data, get)) {
          return id;
        }
        set((state) => {
          const prev: Partial<ExtendedUploadItem> = state.items[id] || {};
          const nextFlow =
            data.flow ??
            prev.flow ??
            (data.kind === "download" ? "download" : undefined);
          const nextStatus = data.status ?? prev.status ?? "processing";
          const previousUiStage = getObjectImportUiStage(prev.status);
          const nextUiStage = getObjectImportUiStage(nextStatus);
          const shouldResetObjectImportUiStage =
            nextFlow === "editor_object_import" &&
            nextUiStage != null &&
            previousUiStage !== nextUiStage;
          const objectImportUiStageStartedAt =
            data.objectImportUiStageStartedAt ??
            (shouldResetObjectImportUiStage
              ? Date.now()
              : (prev.objectImportUiStageStartedAt ??
                (nextFlow === "editor_object_import" && nextUiStage != null
                  ? Date.now()
                  : null)));
          const merged: ExtendedUploadItem = {
            id,
            fileName: data.fileName ?? prev.fileName ?? "file",
            fileUrl: data.fileUrl ?? prev.fileUrl ?? null,
            storagePath: data.storagePath ?? prev.storagePath ?? null,
            // 추가: 변환 산출물의 GCS 경로 저장
            gcsPath: data.gcsPath ?? prev.gcsPath ?? null,
            size: data.size ?? prev.size ?? null,
            projectTitle: data.projectTitle ?? prev.projectTitle ?? null, // 프로젝트 타이틀 저장
            status: nextStatus,
            progress:
              typeof data.progress === "number"
                ? data.progress
                : (prev.progress ?? 0),
            error: data.error ?? null,
            paused: data.paused ?? prev.paused ?? false,
            cancelled: data.cancelled ?? prev.cancelled ?? false,
            originViewrGcsPath:
              data.originViewrGcsPath ?? prev.originViewrGcsPath ?? null,
            originViewrTitle:
              data.originViewrTitle ?? prev.originViewrTitle ?? null,
            originSpaceIndex:
              data.originSpaceIndex ?? prev.originSpaceIndex ?? null,
            originRoute: data.originRoute ?? prev.originRoute ?? null,
            // 완료된 파일의 상세 정보 저장
            finalGcsPath: data.finalGcsPath ?? prev.finalGcsPath ?? null,
            convertedDownloadUrl:
              data.convertedDownloadUrl ?? prev.convertedDownloadUrl ?? null,
            jobId: data.jobId ?? prev.jobId ?? null,
            // 승인 code는 그 Job에만 속한다. 다른 Job으로 바뀌면 이전 값을 넘기지 않는다.
            conversionLogTest:
              data.conversionLogTest !== undefined
                ? data.conversionLogTest
                : data.jobId && data.jobId !== prev.jobId
                  ? null
                  : (prev.conversionLogTest ?? null),
            platformFiles: data.platformFiles ?? prev.platformFiles ?? null,
            fileData: (data.fileData ?? prev.fileData ?? null) as Record<
              string,
              unknown
            > | null,
            kind: data.kind ?? prev.kind ?? "asset",
            flow: nextFlow,
            targetSpaceIndex:
              data.targetSpaceIndex ?? prev.targetSpaceIndex ?? null, // 업로드 시작 시점의 Space index
            targetSpaceName:
              data.targetSpaceName ?? prev.targetSpaceName ?? null,
            targetSpaceCaptureSource:
              data.targetSpaceCaptureSource ??
              prev.targetSpaceCaptureSource ??
              null,
            targetSpaceCapturedTransactionId:
              data.targetSpaceCapturedTransactionId ??
              prev.targetSpaceCapturedTransactionId ??
              null,
            targetSpaceCapturedPhase:
              data.targetSpaceCapturedPhase ??
              prev.targetSpaceCapturedPhase ??
              null,
            storageUsageSnapshotBytes:
              data.storageUsageSnapshotBytes ??
              prev.storageUsageSnapshotBytes ??
              null,
            editorImportVseIndex:
              data.editorImportVseIndex ?? prev.editorImportVseIndex ?? null,
            editorImportRuntimeState:
              data.editorImportRuntimeState !== undefined ? data.editorImportRuntimeState :
              prev.editorImportRuntimeState ?? null,
            editorImportAdmission: data.editorImportAdmission ?? prev.editorImportAdmission ?? null,
            objectImportUiStageStartedAt,
            loaderValidation:
              data.loaderValidation ?? prev.loaderValidation ?? null,
            conversionFailure:
              data.conversionFailure ?? prev.conversionFailure ?? null,
            timestamps: {
              createdAt: prev.timestamps?.createdAt ?? Date.now(),
              updatedAt: Date.now(),
            },
          };
          // Admission's original slot never follows later current-context repairs.
          if (prev.editorImportAdmission) {
            merged.originViewrGcsPath = prev.originViewrGcsPath;
            merged.originSpaceIndex = prev.originSpaceIndex;
            merged.targetSpaceIndex = prev.targetSpaceIndex!;
            merged.targetSpaceName = prev.targetSpaceName;
          }
          return { items: { ...state.items, [id]: merged } };
        });
        return id;
      },

      startUpload: ({
        id,
        fileName,
        storagePath,
        gcsPath,
        size,
        projectTitle,
        targetSpaceIndex,
        targetSpaceName,
        originViewrGcsPath,
        originViewrTitle,
        originSpaceIndex,
        originRoute,
        storageUsageSnapshotBytes,
        kind,
        flow,
        targetSpaceCaptureSource,
        targetSpaceCapturedTransactionId,
        targetSpaceCapturedPhase,
        editorImportVseIndex,
        editorImportRuntimeState,
      }) => {
        const finalId = id || fallbackId({ storagePath, fileName });
        return get().upsertItem({
          id: finalId,
          fileName,
          storagePath: storagePath ?? null,
          gcsPath: gcsPath ?? null,
          size: size ?? null,
          projectTitle: projectTitle ?? null,
          targetSpaceIndex: targetSpaceIndex ?? null, // 업로드 시작 시점의 Space index 저장
          targetSpaceName: targetSpaceName ?? null,
          targetSpaceCaptureSource: targetSpaceCaptureSource ?? null,
          targetSpaceCapturedTransactionId:
            targetSpaceCapturedTransactionId ?? null,
          targetSpaceCapturedPhase: targetSpaceCapturedPhase ?? null,
          originViewrGcsPath: originViewrGcsPath ?? null,
          originViewrTitle: originViewrTitle ?? null,
          originSpaceIndex: originSpaceIndex ?? null,
          originRoute: originRoute ?? null,
          storageUsageSnapshotBytes: storageUsageSnapshotBytes ?? null,
          editorImportVseIndex: editorImportVseIndex ?? null,
          editorImportRuntimeState: editorImportRuntimeState ?? null,
          kind: kind ?? "asset",
          flow: flow ?? (kind === "viewr" ? "viewr_upload" : undefined),
          status: "uploading",
          progress: 0,
        });
      },

      startDownload: ({ id, fileName, gcsPath, flow }) => {
        const finalId = id || fallbackId({ fileUrl: gcsPath, fileName });
        return get().upsertItem({
          id: finalId,
          fileName,
          gcsPath: gcsPath ?? null,
          kind: "download",
          flow: flow ?? "download",
          status: "downloading",
          progress: 0,
        });
      },

      setUploaded: ({ id, storagePath }) => {
        if (id && !get().items[id]) return id;
        const finalId = id || fallbackId({ storagePath });
        const prev = get().items[finalId];
        if (prev?.flow === "editor_object_import") {
          return get().upsertItem({
            id: finalId,
            storagePath,
            status: "uploading",
            progress: prev.progress ?? 0,
          });
        }
        // Uploading(0~25%) 완료 → Converting 시작 전, 최소 25% 유지
        const minProgress = 25;
        const nextProgress = Math.max(minProgress, prev?.progress ?? 0);
        return get().upsertItem({
          id: finalId,
          storagePath,
          status: "uploaded",
          progress: nextProgress,
        });
      },

      setConverting: ({ id, jobId, storagePath, progress }) => {
        if (id && !get().items[id]) return id;
        const finalId = id || jobId || fallbackId({ storagePath });
        const prev = get().items[finalId];
        if (prev?.flow === "editor_object_import") {
          const previousRank = getObjectImportStatusRank(prev.status);
          const nextRank = getObjectImportStatusRank("converting");
          if (
            previousRank != null &&
            nextRank != null &&
            previousRank > nextRank
          ) {
            return finalId;
          }
          return get().upsertItem({
            id: finalId,
            status: "converting",
            jobId,
            storagePath,
            progress: getObjectImportMilestone("converting") ?? 20,
          });
        }
        // Converting 단계 시작 시 최소 25% (Uploading 0~25% 이후)
        const minProgress = 25;
        const nextProgress =
          typeof progress === "number"
            ? Math.max(minProgress, progress)
            : Math.max(minProgress, prev?.progress ?? 0);
        return get().upsertItem({
          id: finalId,
          status: "converting",
          jobId,
          storagePath,
          progress: nextProgress,
        });
      },

      setOptimizing: ({ id }) => {
        const prev = get().items[id];
        if (prev?.flow === "editor_object_import") {
          const previousRank = getObjectImportStatusRank(prev.status);
          const nextRank = getObjectImportStatusRank("optimizing");
          if (
            previousRank != null &&
            nextRank != null &&
            previousRank > nextRank
          ) {
            return id;
          }
          return get().upsertItem({
            id,
            status: "optimizing",
            progress: getObjectImportMilestone("optimizing") ?? 40,
          });
        }
        return get().upsertItem({ id, status: "optimizing" });
      },

      setBundling: ({ id }) => {
        const prev = get().items[id];
        // Processing 단계 (65~95%), bundling은 75%
        const nextProgress = Math.max(75, prev?.progress ?? 0);
        return get().upsertItem({
          id,
          status: "bundling",
          progress: nextProgress,
        });
      },

      setRendering: ({ id, progress }) => {
        const prev = get().items[id];
        if (prev?.flow === "editor_object_import") {
          const previousRank = getObjectImportStatusRank(prev.status);
          const nextRank = getObjectImportStatusRank("rendering");
          if (
            previousRank != null &&
            nextRank != null &&
            previousRank > nextRank
          ) {
            return id;
          }
          return get().upsertItem({
            id,
            status: "rendering",
            progress: getObjectImportMilestone("rendering") ?? 60,
          });
        }
        const nextProgress =
          typeof progress === "number"
            ? Math.max(80, progress)
            : Math.max(80, prev?.progress ?? 0);
        return get().upsertItem({
          id,
          status: "rendering",
          progress: Math.min(99, nextProgress),
        });
      },

      setFinalizing: ({ id }) => {
        const prev = get().items[id];
        if (prev?.flow !== "editor_object_import") {
          return get().upsertItem({ id, status: "finalizing", progress: 80 });
        }
        const previousRank = getObjectImportStatusRank(prev.status);
        const nextRank = getObjectImportStatusRank("finalizing");
        if (
          previousRank != null &&
          nextRank != null &&
          previousRank > nextRank
        ) {
          return id;
        }
        return get().upsertItem({
          id,
          status: "finalizing",
          progress: getObjectImportMilestone("finalizing") ?? 80,
        });
      },

      setProcessing: ({ id, message, progress }) => {
        return get().upsertItem({
          id,
          status: "processing",
          progress: progress ?? 0,
        });
      },

      setProgress: ({ id, progress }) => {
        const prev = get().items[id];
        if (prev?.flow === "editor_object_import") {
          return id;
        }
        return get().upsertItem({ id, progress });
      },

      complete: ({
        id,
        fileUrl,
        gcsPath,
        finalGcsPath,
        convertedDownloadUrl,
        jobId,
        platformFiles,
        fileData,
        kind,
      }) => {
        const prev = get().items[id];
        if (prev?.flow === "editor_object_import") {
          const previousRank = getObjectImportStatusRank(prev.status);
          const completedRank = getObjectImportStatusRank("completed");
          if (
            previousRank != null &&
            completedRank != null &&
            previousRank > completedRank
          ) {
            return id;
          }
          get().upsertItem({ id, editorImportRuntimeState: "completed" });
        }
        return get().upsertItem({
          id,
          status: "completed",
          editorImportRuntimeState:
            prev?.flow === "editor_object_import"
              ? "completed"
              : (prev?.editorImportRuntimeState ?? null),
          progress: 100,
          fileUrl: fileUrl ?? null,
          gcsPath: gcsPath ?? null,
          finalGcsPath: finalGcsPath ?? gcsPath ?? null,
          convertedDownloadUrl: convertedDownloadUrl ?? null,
          jobId: jobId ?? null,
          platformFiles: platformFiles ?? null,
          fileData: fileData ?? null,
          kind: kind ?? "asset",
        });
      },

      fail: ({ id, error, status }) => {
        const item = get().items[id];
        if (!item) return id;
        if (
          item.flow === "editor_object_import" &&
          item.editorImportRuntimeState === "completed"
        ) {
          return id;
        }
        const finalStatus = status ?? "failed";
        const runtimeFailure = item.status === "rendering" || item.status === "finalizing";
        const failureStage = getObjectImportUiStage(item.status);
        const importFailure: ConversionFailureResultSummary | undefined =
          finalStatus === "failed" &&
          (item.flow === "editor_object_import" || item.flow === "editor_library_attach") &&
          !item.conversionFailure &&
          item.loaderValidation?.status !== "failed"
            ? {
                source: runtimeFailure ? "runtime" : "converter",
                status: "failed",
                stage: failureStage === "completed" ? "rendering" : failureStage ?? "uploading",
                fileName: item.fileName,
              }
            : undefined;
        const result = get().upsertItem({
          id,
          status: finalStatus,
          conversionFailure: importFailure,
          editorImportRuntimeState:
            item.flow === "editor_object_import"
              ? "failed"
              : (item.editorImportRuntimeState ?? null),
          error,
          progress: 0,
        });
        try {
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("upload:workFailed", {
                detail: { id, error, status: finalStatus },
              }),
            );
          }
        } catch (_) {
          /* ignore */
        }
        return result;
      },

      pause: (id: string) => {
        return get().upsertItem({ id, paused: true, status: "paused" });
      },

      resume: (id: string) => {
        const item = get().items[id];
        if (!item) return;
        if (item.flow === "editor_object_import") {
          const resumeStatus: UploadStatus =
            item.progress < 20
              ? "uploading"
              : item.progress < 40
                ? "converting"
                : item.progress < 60
                  ? "optimizing"
                  : item.progress < 80
                    ? "rendering"
                    : "finalizing";
          return get().upsertItem({
            id,
            paused: false,
            status: resumeStatus,
            objectImportUiStageStartedAt: Date.now(),
          });
        }
        // Resume to previous status (단계별 범위: 25, 65, 95)
        const resumeStatus: UploadStatus =
          item.progress < 25
            ? "uploading"
            : item.progress >= 53 && item.progress < 65
              ? "optimizing"
              : item.progress < 65
                ? "converting"
                : item.status === "rendering"
                  ? "rendering"
                  : "processing";
        return get().upsertItem({ id, paused: false, status: resumeStatus });
      },

      cancel: (id: string) => {
        const item = get().items[id];
        if (!item) return id;
        if (hasEditorImportAdmission(item)) {
          const admission = item.editorImportAdmission!;
          get().upsertItem({ id, editorImportAdmission: { ...admission,
            executionId: createEditorImportIdentity(),
            state: admission.remotePhase === "not_submitted" ? "released" : "reconciling" } });
        }
        const result = get().upsertItem({
          id,
          cancelled: true,
          status: "cancelled",
          error: "Cancelled by user",
        });
        try {
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("upload:workCancelled", { detail: { id } }),
            );
          }
        } catch (_) {
          /* ignore */
        }
        return result;
      },

      remove: (id: string) => {
        const item = get().items[id];
        if (hasEditorImportAdmission(item)) {
          get().upsertItem({ id, editorImportAdmission: { ...item.editorImportAdmission!, dismissed: true } });
          return;
        }
        clearDeferredObjectImportQueue(id);
        set((state) => {
          const items = { ...state.items };
          delete items[id];
          return { items };
        });
      },

      clearAll: () => {
        clearAllDeferredObjectImportQueues();
        set((state) => ({ items: Object.fromEntries(Object.entries(state.items).filter(([, item]) => hasEditorImportAdmission(item))) }));
      },

      // 사용자가 닫은 종료 항목과 복구할 서버 상태가 없는 멈춘 항목 정리.
      // 종료 결과는 사용자가 닫을 때까지 유지하고(STUDIO-ASSET-003 TransferPanel),
      // 서버 Job을 추적하는 변환 항목은 monitor가 서버 상태로 마감하므로 시간으로 지우지 않는다.
      cleanupOldItems: () =>
        set((state) => {
          const now = Date.now();
          const ONE_HOUR = 60 * 60 * 1000; // 1시간
          const items = { ...state.items };
          let cleanedCount = 0;

          Object.keys(items).forEach((id) => {
            const item = items[id];
            if (hasEditorImportAdmission(item)) return;
            const isOld = now - (item.timestamps?.updatedAt || 0) > ONE_HOUR;
            const isTerminal = [
              "completed",
              "failed",
              "failed_capacity_post_conversion",
              "cancelled",
            ].includes(item.status);
            // 예약 중에 닫은 항목은 remove()가 dismissed로만 표시한다. 예약이 풀리면 여기서 지운다.
            const isDismissedTerminal = isTerminal && item.editorImportAdmission?.dismissed === true;
            const isServerMonitored =
              item.status === "converting" &&
              Boolean(item.jobId) &&
              !skipsFileConversionJobMonitoring(item.flow) &&
              (!item.editorImportAdmission || item.editorImportAdmission.remotePhase === "accepted");
            const isStuck =
              [
                "processing",
                "converting",
                "bundling",
                "rendering",
                "finalizing",
                "downloading",
              ].includes(item.status) && isOld && !isServerMonitored;

            if (isDismissedTerminal || isStuck) {
              delete items[id];
              cleanedCount++;
            }
          });

          if (cleanedCount > 0) {
            console.log(
              `[UploadStore] 🧹 ${cleanedCount}개의 오래된/멈춘 아이템 정리됨`,
            );
          }

          return { items };
        }),
    }),
    {
      name: "upload-store",
      storage: createSafeLocalStorage<PersistedUploadStoreState>(),
      version: UPLOAD_STORE_PERSIST_VERSION,
      partialize: partializeUploadStore,
      migrate: migrateUploadStorePersistedState,
    },
  ),
);

// Selectors
export const selectors = {
  all: (state: UploadStoreState): ExtendedUploadItem[] =>
    Object.values(state.items).sort(
      (a, b) => (b.timestamps?.updatedAt || 0) - (a.timestamps?.updatedAt || 0),
    ),
  byId:
    (id: string) =>
    (state: UploadStoreState): ExtendedUploadItem | undefined =>
      state.items[id],
  byStatus:
    (status: UploadStatus) =>
    (state: UploadStoreState): ExtendedUploadItem[] =>
      Object.values(state.items).filter((i) => i.status === status),
};
